import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { CsvButton } from '@/components/CsvButton'
import { DaysSelect } from '@/components/DaysSelect'
import { RefreshButton } from '@/components/RefreshButton'
import { downloadCsv } from '@/lib/csv'

// A16.37 — Moderatsiya KPI (`reports` tizimi). Modul-lokal, alohida fayl (band-fayllar
// AnalyticsMetrics/Screens boshqa sessiyalar tomonidan tahrirlanayotgani uchun izolyatsiya).
// Backend: GET /admin/analytics/moderation (`handlers/admin.go` AnalyticsModeration). PII-siz.
interface ModerationKpiData {
  pending: number
  resolved: number
  dismissed: number
  total: number
  created_7d: number
  resolved_7d: number
  avg_resolution_hours: number
  by_category: { category: string; count: number }[]
  per_day: { day: string; count: number }[]
}
function fetchModeration(days: number, signal?: AbortSignal): Promise<ModerationKpiData> {
  return api.get<ModerationKpiData>(`/admin/analytics/moderation?days=${days}`, signal ? { signal } : undefined)
}

/** Soatni qisqa mahalliy davomiylikka aylantiradi (analytics.durUnits qayta ishlatiladi). */
function fmtDuration(hours: number, u: { d: string; h: string; m: string }): string {
  if (!Number.isFinite(hours) || hours <= 0) return '—'
  const totalMin = Math.round(hours * 60)
  const d = Math.floor(totalMin / 1440)
  const h = Math.floor((totalMin % 1440) / 60)
  const m = totalMin % 60
  if (d > 0) return `${d}${u.d} ${h}${u.h}`
  if (h > 0) return `${h}${u.h} ${m}${u.m}`
  return `${m}${u.m}`
}

function KPI({ label, value, loading, accent }: { label: string; value?: number; loading: boolean; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function StatTile({ label, value, loading }: { label: string; value?: string; loading: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className="mt-0.5 font-mono text-2xl tabular text-primary">{value ?? '—'}</div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** A16.37 — Moderatsiya KPI (shikoyat holati/oqim/hal-vaqti + kategoriya/trend). */
export function ModerationKpi() {
  const t = useT()
  const [data, setData] = useState<ModerationKpiData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [days, setDays] = useState(14)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchModeration(days, ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload, days])
  const catBars = useMemo<BarDatum[]>(
    () => (data?.by_category ?? []).map((c) => ({ label: c.category, value: c.count })),
    [data],
  )
  const trendBars = useMemo<BarDatum[]>(
    () => (data?.per_day ?? []).map((d) => ({ label: d.day.slice(5), value: d.count, title: d.day })),
    [data],
  )
  const handleExport = () => {
    if (!data) return
    downloadCsv('moderation-kpi', [
      [t.analytics.modPending, t.analytics.modResolved, t.analytics.modDismissed, t.analytics.modTotal, t.analytics.modCreated7d, t.analytics.modResolved7d],
      [data.pending, data.resolved, data.dismissed, data.total, data.created_7d, data.resolved_7d],
      [],
      [t.analytics.modByCategory, t.analytics.modTotal],
      ...data.by_category.map((c) => [c.category, c.count]),
    ])
  }
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={t.analytics.moderationKpi}
        screenId="A16.37"
        actions={
          <div className="flex items-center gap-2">
            <DaysSelect value={days} onChange={setDays} />
            <RefreshButton onClick={() => setReload((n) => n + 1)} />
            {data && <CsvButton onClick={handleExport} />}
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={t.analytics.modPending} value={data?.pending} loading={loading} accent />
          <KPI label={t.analytics.modResolved} value={data?.resolved} loading={loading} />
          <KPI label={t.analytics.modDismissed} value={data?.dismissed} loading={loading} />
          <KPI label={t.analytics.modTotal} value={data?.total} loading={loading} />
          <KPI label={t.analytics.modCreated7d} value={data?.created_7d} loading={loading} />
          <KPI label={t.analytics.modResolved7d} value={data?.resolved_7d} loading={loading} />
          <StatTile
            label={t.analytics.modAvgResolution}
            value={data ? fmtDuration(data.avg_resolution_hours, t.analytics.durUnits) : undefined}
            loading={loading}
          />
        </div>
        <Panel title={t.analytics.modByCategory}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={catBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
        <Panel title={`${t.analytics.modTrend} · ${t.analytics.daysLabel(days)}`}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={trendBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
          )}
        </Panel>
      </div>
    </>
  )
}
